import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasInternalTransferOptions } from "../../../../Hooks/useBaasInternalTransferOptions";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import {
  GRID_HISTORY_COLS_8,
  GRID_HISTORY_COLS_9,
  GridHistoryCard,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryDetailButton,
  GridHistoryHeader,
  GridHistoryRow,
  GridPagination,
} from "../../../../Components/Molecules/GridHistory";
import {
  getAdditionalCurrencyCount,
  fetchBaasAccounts,
  fetchBaasPeerInstanceRoots,
  getPrimaryBalance,
  formatBaasAvailableWithLimit,
  isBaasExitRailVirtualAccount,
  isBaasInstanceOwnerAccount,
  isContasOperationalTableExcludedShell,
  isVirtualInstanceShellAccount,
  resolveBaasInstanceOwnerEntityId,
} from "../../../../Services/baasAccountsListApi";
import { formatBaasInstanceCredentialError } from "../../../../Services/baasConsoleClient";
import { formatMonetaryValue } from "../../../../Utils/formatters";
import { isUnavailablePldProposalSituation } from "../../../../Utils/baasProposalSituation";
import { paginateList } from "../../Extract/extractFilters";
import { hasBaasBankingRails } from "../baasCapabilities";
import { BaasInstanceNameCell } from "../components/BaasInstanceNameCell";
import {
  canUseBaasGlobalInstanceFilter,
  resolveBaasListInstanceNumericId,
  rowMatchesBaasListInstanceFilter,
  shouldFetchBaasPeerLists,
  stampBaasRowInstance,
} from "../baasInstanceListFilter";
import { shouldPinPeerForAccountOps } from "../baasPeerOpsPin";
import { BAAS_ROOT_PATH, buildBaasAccountDetailPath } from "../constants";
import { EMPTY_BAAS_ACCOUNTS_FILTERS, filterBaasAccounts } from "./baasAccountsFilters";
import { BaasAccountCreateModal } from "./components/BaasAccountCreateModal";
import { BaasAccountsFilterBar } from "./components/BaasAccountsFilterBar";
import { BaasAccountsQuickActions } from "./components/BaasAccountsQuickActions";
import { BaasAccountStatusBadge } from "./components/BaasAccountStatusBadge";
import { BaasProposalSituationBadge } from "../components/BaasProposalSituationBadge";
import { fetchBaasInstanceDetail } from "../../../../Services/baasInstancesApi";

const displayNameUpper = (value) => {
  const text = String(value || "").trim();
  return text ? text.toUpperCase() : "—";
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "short" }).format(date);
};

const formatBalanceSummary = (account) => {
  const primary = getPrimaryBalance(account.balances);
  if (!primary) {
    return intl.get("baas.accounts.noBalances");
  }

  const primaryLabel = formatBaasAvailableWithLimit(primary);
  const extraCount = getAdditionalCurrencyCount(account.balances);

  if (extraCount === 0) {
    return primaryLabel;
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span>{primaryLabel}</span>
      <span className="inline-flex rounded-full bg-[#fff8f0] px-2 py-0.5 text-[11px] font-semibold text-[#9a7340] dark:bg-[#2a2a2a] dark:text-brand-gold-mid">
        {intl.get("baas.accounts.additionalCurrencies", { count: extraCount })}
      </span>
    </span>
  );
};

const getBalanceSummaryTitle = (account) => {
  const primary = getPrimaryBalance(account.balances);
  if (!primary) return intl.get("baas.accounts.noBalances");
  const primaryLabel = formatBaasAvailableWithLimit(primary);
  const extraCount = getAdditionalCurrencyCount(account.balances);
  if (extraCount === 0) return primaryLabel;
  return `${primaryLabel} · ${intl.get("baas.accounts.additionalCurrencies", {
    count: extraCount,
  })}`;
};

const AccountsHeaderRow = ({ showInstanceColumn }) => (
  <GridHistoryHeader colSpan={showInstanceColumn ? 9 : 8}>
    <span>{intl.get("baas.accounts.columns.entity")}</span>
    <span>{intl.get("baas.accounts.columns.account")}</span>
    <span>{intl.get("baas.accounts.columns.type")}</span>
    {showInstanceColumn ? (
      <span>{intl.get("baas.accounts.columns.instance")}</span>
    ) : null}
    <span>{intl.get("baas.accounts.columns.balances")}</span>
    <span>{intl.get("baas.accounts.columns.createdAt")}</span>
    <span className="whitespace-nowrap">{intl.get("baas.accounts.columns.statusAccount")}</span>
    <span className="whitespace-nowrap">{intl.get("baas.accounts.columns.statusProposal")}</span>
    <span className="text-right">{intl.get("baas.accounts.columns.actions")}</span>
  </GridHistoryHeader>
);

const AccountRow = ({ account, onOpen, showInstanceColumn }) => {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(account);
    }
  };

  // CONTA: one number line; secondary only when alias/id differs (map often sets alias = accountNumber).
  const accountPrimary = String(
    account.isInstanceRoot
      ? account.accountNumber || account.alias || account.id || ""
      : account.accountNumber || account.alias || "",
  ).trim();
  const accountSecondaryCandidate = String(
    account.isInstanceRoot ? account.id || "" : account.alias || "",
  ).trim();
  const accountSecondary =
    accountSecondaryCandidate && accountSecondaryCandidate !== accountPrimary
      ? accountSecondaryCandidate
      : "";

  return (
    <GridHistoryRow
      colSpan={showInstanceColumn ? 9 : 8}
      className="cursor-pointer hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(account)}
      onKeyDown={handleKeyDown}
    >
      <GridHistoryCell label={intl.get("baas.accounts.columns.entity")}>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
            {displayNameUpper(account.entityName)}
          </p>
        </div>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.accounts.columns.account")}>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
            {accountPrimary || "—"}
          </p>
          {accountSecondary ? (
            <p className="truncate font-mono text-xs text-gray-500 dark:text-gray-400">
              {accountSecondary}
            </p>
          ) : null}
        </div>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.accounts.columns.type")}>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {account.isInstanceRoot || account.type === "virtual" || account.type === "root"
            ? intl.get("baas.accounts.type.virtual")
            : account.type === "operational"
              ? intl.get("baas.accounts.type.operational")
              : intl.get(`baas.accounts.type.${account.type}`)}
        </p>
      </GridHistoryCell>
      {showInstanceColumn ? (
        <GridHistoryCell label={intl.get("baas.accounts.columns.instance")}>
          <BaasInstanceNameCell name={account.instanceName || account.programCode} />
        </GridHistoryCell>
      ) : null}
      <GridHistoryCell label={intl.get("baas.accounts.columns.balances")}>
        <div
          className="truncate text-sm text-gray-600 dark:text-gray-300"
          title={getBalanceSummaryTitle(account)}
        >
          {formatBalanceSummary(account)}
        </div>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.accounts.columns.createdAt")}>
        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(account.createdAt)}</p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.accounts.columns.statusAccount")}>
        <BaasAccountStatusBadge status={account.status} />
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.accounts.columns.statusProposal")} className="whitespace-nowrap">
        {account.proposalSituation && !isUnavailablePldProposalSituation(account.proposalSituation) ? (
          <BaasProposalSituationBadge situation={account.proposalSituation} />
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.accounts.columns.actions")} align="right">
        <GridHistoryDetailButton
          onClick={(event) => {
            event.stopPropagation();
            onOpen(account);
          }}
        />
      </GridHistoryCell>
    </GridHistoryRow>
  );
};

export const BaasAccountsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { addToast } = useToast();
  const { selectedInstance, dataEpoch, selectInstanceForPeerOps, clearPeerOpsPin, instances, listInstanceFilter } = useBaasInstance();

  // Contas list stays on sidebar pin — clear soft peer ops leftover from detail.
  useEffect(() => {
    clearPeerOpsPin?.();
  }, [clearPeerOpsPin]);
  const { can, capabilities, loading: capabilitiesLoading } = useBaasCapabilities();
  const canCreateAccount = can("accounts.create");
  const canTransfer = can("accounts.transfer");
  const isLoading = useBaasPageLoading(
    selectedInstance?.id ? `${selectedInstance.id}:${dataEpoch}` : null,
  );
  const {
    loading: transferOptionsLoading,
    error: transferOptionsError,
    sourceAccounts: transferSourceAccounts,
    destinationAccounts: transferDestinationAccounts,
    virtualAccounts: transferVirtualAccounts,
    canTransferOut,
  } = useBaasInternalTransferOptions();
  const [filters, setFilters] = useState(EMPTY_BAAS_ACCOUNTS_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [listVersion, setListVersion] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [createEntityId, setCreateEntityId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsLoadError, setAccountsLoadError] = useState(null);
  const [ownerEntityId, setOwnerEntityId] = useState(() =>
    resolveBaasInstanceOwnerEntityId(selectedInstance),
  );

  /** Prefer real partner-key failures over a misleading "Sem permissão" gate. */
  const credentialLoadError = useMemo(() => {
    const fromAccounts = formatBaasInstanceCredentialError(accountsLoadError);
    if (fromAccounts) return fromAccounts;
    return formatBaasInstanceCredentialError(transferOptionsError);
  }, [accountsLoadError, transferOptionsError]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpen = params.get("create") === "1" || params.get("create") === "true";
    if (!shouldOpen || !canCreateAccount) return;

    setCreateEntityId(params.get("entityId") || "");
    setCreateOpen(true);
    params.delete("create");
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  }, [canCreateAccount, history, location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setOwnerEntityId("");
      return undefined;
    }

    const fromSelection = resolveBaasInstanceOwnerEntityId(selectedInstance);
    if (fromSelection) {
      setOwnerEntityId(fromSelection);
      return undefined;
    }

    (async () => {
      try {
        const detail = await fetchBaasInstanceDetail(selectedInstance);
        if (!cancelled) {
          setOwnerEntityId(resolveBaasInstanceOwnerEntityId(detail));
        }
      } catch {
        if (!cancelled) setOwnerEntityId("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, dataEpoch]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setAccounts([]);
      setAccountsLoadError(null);
      return undefined;
    }

    setAccounts([]);
    setAccountsLoading(true);
    setAccountsLoadError(null);
    (async () => {
      try {
        const [next, peerRoots] = await Promise.all([
          fetchBaasAccounts(selectedInstance, {
            includeBalances: true,
            onVirtualsError: (error) => {
              if (cancelled) return;
              const friendly = formatBaasInstanceCredentialError(error) || error;
              setAccountsLoadError(friendly);
              addToast({
                type: "error",
                title:
                  friendly?.detail
                  || friendly?.message
                  || intl.get("baas.accounts.virtualSection.loadFailed"),
              });
            },
          }),
          shouldFetchBaasPeerLists(selectedInstance, listInstanceFilter)
            ? fetchBaasPeerInstanceRoots(selectedInstance, {
                instanceId: resolveBaasListInstanceNumericId(
                  listInstanceFilter,
                  selectedInstance,
                  instances,
                ),
              }).catch(() => [])
            : Promise.resolve([]),
        ]);
        if (!cancelled) {
          const byId = new Map();
          for (const row of next) {
            if (!row?.id) continue;
            byId.set(row.id, stampBaasRowInstance(row, selectedInstance));
          }
          for (const row of peerRoots) {
            if (!row?.id) continue;
            byId.set(row.id, { ...row, isPeerInstanceAccount: true });
          }
          setAccounts([...byId.values()]);
        }
      } catch (error) {
        if (!cancelled) {
          const friendly = formatBaasInstanceCredentialError(error) || error;
          setAccounts([]);
          setAccountsLoadError(friendly);
          addToast({
            type: "error",
            title:
              friendly?.detail
              || friendly?.message
              || intl.get("baas.accounts.loadFailed")
              || intl.get("baas.accounts.virtualSection.loadFailed"),
          });
        }
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, dataEpoch, listVersion, addToast, listInstanceFilter, instances]);

  const handleAccountCreated = () => {
    setListVersion((current) => current + 1);
    setPage(1);
  };

  /**
   * Contas de operação exclusions — OPERATING/OVERDRAFT/PROGRAM/Root ids from
   * transfer-options (explicit `role`). Shells live under Root menu.
   */
  const operationShellIds = useMemo(() => {
    const ids = new Set();
    for (const account of [
      ...(transferSourceAccounts || []),
      ...(transferDestinationAccounts || []),
      ...(transferVirtualAccounts || []),
    ]) {
      if (!isContasOperationalTableExcludedShell(account)) continue;
      const id = String(account?.id || "").trim();
      if (id) ids.add(id);
    }
    return ids;
  }, [
    transferSourceAccounts,
    transferDestinationAccounts,
    transferVirtualAccounts,
  ]);

  const showInstanceColumn = canUseBaasGlobalInstanceFilter(selectedInstance);

  /** Client/partner Conta Digital only. Virtual/shell roots live under Contas virtuais. */
  const operationalAccounts = useMemo(
    () =>
      accounts.filter((account) => {
        if (account?.isPeerInstanceAccount) return true;
        if (account?.isInstanceRoot) return false;
        if (isBaasExitRailVirtualAccount(account)) return false;
        if (isVirtualInstanceShellAccount(account)) return false;
        if (isBaasInstanceOwnerAccount(account, ownerEntityId)) return false;
        const id = String(account?.id || "").trim();
        if (id && operationShellIds.has(id)) return false;
        return true;
      }),
    [accounts, operationShellIds, ownerEntityId],
  );

  const filteredItems = useMemo(
    () =>
      filterBaasAccounts(operationalAccounts, filters).filter((row) =>
        rowMatchesBaasListInstanceFilter(
          row,
          listInstanceFilter,
          selectedInstance,
          instances,
        ),
      ),
    [operationalAccounts, filters, listInstanceFilter, selectedInstance, instances],
  );

  const paginatedItems = useMemo(
    () => paginateList(filteredItems, page, rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const openAccountDetail = (account) => {
    // Peer Conta Digital / cross-tenant row: pin console to that tenant so
    // X-Instance-Id + elevate keep backoffice mutations isolated.
    if (shouldPinPeerForAccountOps(account, selectedInstance)) {
      selectInstanceForPeerOps?.(
        account.instanceId ?? account.baasInstanceId,
        selectedInstance?.environment,
      );
    }
    history.push(buildBaasAccountDetailPath(account.id));
  };

  if (isLoading || capabilitiesLoading || accountsLoading) {
    return <BaasTablePageSkeleton withQuickActions columns={8} />;
  }

  // Cards-only (emissor): shells/owner live under Root — Contas is banking clients only.
  if (!hasBaasBankingRails(capabilities)) {
    return <Redirect to={BAAS_ROOT_PATH} />;
  }

  if (credentialLoadError) {
    const message =
      credentialLoadError.detail
      || credentialLoadError.message
      || intl.get("baas.accounts.operationShellsSection.apiKeyInvalid")
      || intl.get("baas.accounts.virtualSection.apiKeyInvalid");
    return (
      <div className="baas-accounts min-w-0 space-y-5">
        <header>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.accounts")}
          </h1>
        </header>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          {message}
        </div>
      </div>
    );
  }

  if (!can("accounts.view")) {
    return (
      <div className="baas-accounts min-w-0 space-y-5">
        <header>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.accounts")}
          </h1>
        </header>
        <div className="app-card px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.home.capabilities.permissionDenied")}
        </div>
      </div>
    );
  }

  return (
    <div className="baas-accounts min-h-0 min-w-0 h-auto space-y-5">
      <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.accounts")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.accounts.subtitle")}
          </p>
        </div>
      </header>

      <BaasAccountsQuickActions
        onCreateClick={() => {
          setCreateEntityId("");
          setCreateOpen(true);
        }}
        createEnabled={canCreateAccount}
        transferVisible={canTransfer}
        transferEnabled={canTransfer && canTransferOut}
        transferOptionsLoading={transferOptionsLoading}
      />

      <div>
        <p className="dashboard-section-label mb-1">
          {intl.get("baas.accounts.operationalSection.section")}
        </p>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.operationalSection.subtitle")}
        </p>
      </div>

      <BaasAccountsFilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filteredItems={filteredItems}
        accounts={operationalAccounts}
      />

      <BaasAccountCreateModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateEntityId("");
        }}
        initialEntityId={createEntityId}
        onCreated={handleAccountCreated}
      />

      <GridHistoryCard>
        {paginatedItems.length === 0 ? (
          <div className="app-empty-state m-4">{intl.get("baas.accounts.empty")}</div>
        ) : (
          <GridHistoryContainer
            gridCols={showInstanceColumn ? GRID_HISTORY_COLS_9 : GRID_HISTORY_COLS_8}
          >
            <AccountsHeaderRow showInstanceColumn={showInstanceColumn} />
            {paginatedItems.map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                onOpen={openAccountDetail}
                showInstanceColumn={showInstanceColumn}
              />
            ))}
          </GridHistoryContainer>
        )}

        <GridPagination
          total={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
          currentPageCount={paginatedItems.length}
          itemLabel={intl.get("baas.accounts.paginationLabel")}
        />
      </GridHistoryCard>
    </div>
  );
};

export default BaasAccountsPage;
